import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ActivityIndicator,
  Pressable,
  SafeAreaView,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useNavigation } from "@react-navigation/native";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";
import type RootStackParamList from "../navigation/RootStackParamList";
import { useAppSelector } from "../redux/store";
import {
  getSignupFunnel,
  type FunnelUser,
  type SignupFunnel,
} from "../services/signupFunnelService";

type GroupKey = "neither" | "created" | "joinedOthers";

const GROUPS: {
  key: GroupKey;
  label: string;
  description: string;
  icon: keyof typeof Ionicons.glyphMap;
}[] = [
  {
    key: "neither",
    label: "Signed up, nothing else",
    description: "Registered but never created or joined a conversation",
    icon: "person-outline",
  },
  {
    key: "created",
    label: "Created a conversation",
    description: "Registered and started at least one of their own",
    icon: "add-circle-outline",
  },
  {
    key: "joinedOthers",
    label: "Joined someone else's",
    description: "Registered and signed up for another person's conversation",
    icon: "people-outline",
  },
];

function PersonRow({ person }: { person: FunnelUser }) {
  return (
    <View style={styles.personRow}>
      <Text style={styles.personName}>{person.name}</Text>
      {person.email ? (
        <Text style={styles.personEmail}>{person.email}</Text>
      ) : null}
    </View>
  );
}

export default function AdminSignupFunnelScreen() {
  const isAdmin = useAppSelector((state) => state.user.admin);
  const navigation =
    useNavigation<NativeStackNavigationProp<RootStackParamList>>();

  const [funnel, setFunnel] = useState<SignupFunnel | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [expanded, setExpanded] = useState<Record<GroupKey, boolean>>({
    neither: true,
    created: false,
    joinedOthers: false,
  });

  const load = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    setErrorText(null);
    try {
      setFunnel(await getSignupFunnel());
    } catch (error) {
      console.error("Failed to load signup funnel:", error);
      setErrorText("Failed to load the signup funnel.");
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    load();
  }, [load]);

  const filteredGroups = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const match = (people: FunnelUser[]) =>
      q
        ? people.filter(
            (person) =>
              person.name.toLowerCase().includes(q) ||
              person.email.toLowerCase().includes(q),
          )
        : people;
    return {
      neither: match(funnel?.neither ?? []),
      created: match(funnel?.created ?? []),
      joinedOthers: match(funnel?.joinedOthers ?? []),
    };
  }, [funnel, searchQuery]);

  if (!isAdmin) {
    return (
      <SafeAreaView style={styles.screen}>
        <View style={styles.accessState}>
          <Ionicons name="lock-closed-outline" size={28} color="#3a6fb1" />
          <Text style={styles.title}>Access restricted</Text>
          <Text style={styles.subtitle}>
            This page is only available to administrators.
          </Text>
          <Pressable
            style={styles.primaryButton}
            onPress={() => navigation.navigate("MeetingsTabs")}
          >
            <Text style={styles.primaryButtonText}>Back to meetings</Text>
          </Pressable>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.screen}>
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          <Pressable
            style={styles.backButton}
            onPress={() =>
              navigation.canGoBack()
                ? navigation.goBack()
                : navigation.navigate("MeetingsTabs")
            }
            accessibilityRole="button"
            accessibilityLabel="Go back"
          >
            <Ionicons name="chevron-back" size={24} color="#3a6fb1" />
          </Pressable>
          <View>
            <Text style={styles.title}>Signup funnel</Text>
            <Text style={styles.subtitle}>
              {funnel ? `${funnel.total} registered users` : "…"}
            </Text>
          </View>
        </View>
        <Pressable
          style={styles.refreshButton}
          onPress={load}
          accessibilityRole="button"
          accessibilityLabel="Refresh signup funnel"
        >
          <Ionicons name="refresh" size={18} color="#3a6fb1" />
        </Pressable>
      </View>

      <View style={styles.searchContainer}>
        <Ionicons
          name="search"
          size={18}
          color="rgba(43,43,47,0.4)"
          style={styles.searchIcon}
        />
        <TextInput
          style={styles.searchInput}
          placeholder="Search by name or email"
          placeholderTextColor="rgba(43,43,47,0.4)"
          value={searchQuery}
          onChangeText={setSearchQuery}
          autoCapitalize="none"
          autoCorrect={false}
        />
        {searchQuery ? (
          <Pressable
            style={styles.clearButton}
            onPress={() => setSearchQuery("")}
          >
            <Ionicons name="close" size={16} color="rgba(43,43,47,0.5)" />
          </Pressable>
        ) : null}
      </View>

      {loading ? (
        <ActivityIndicator style={styles.loader} color="#3a6fb1" />
      ) : null}
      {errorText ? <Text style={styles.errorText}>{errorText}</Text> : null}

      <ScrollView
        contentContainerStyle={styles.listContent}
        showsVerticalScrollIndicator={false}
      >
        {GROUPS.map((group) => {
          const people = filteredGroups[group.key];
          const total = funnel?.[group.key].length ?? 0;
          const isOpen = expanded[group.key];

          return (
            <View key={group.key} style={styles.groupCard}>
              <Pressable
                style={styles.groupHeader}
                onPress={() =>
                  setExpanded((prev) => ({
                    ...prev,
                    [group.key]: !prev[group.key],
                  }))
                }
              >
                <View style={styles.groupIcon}>
                  <Ionicons name={group.icon} size={18} color="#3a6fb1" />
                </View>
                <View style={styles.groupDetails}>
                  <Text style={styles.groupLabel}>
                    {group.label}{" "}
                    <Text style={styles.groupCount}>({total})</Text>
                  </Text>
                  <Text style={styles.groupDescription}>
                    {group.description}
                  </Text>
                </View>
                <Ionicons
                  name={isOpen ? "chevron-up" : "chevron-down"}
                  size={18}
                  color="rgba(43,43,47,0.5)"
                />
              </Pressable>

              {isOpen ? (
                <View style={styles.peopleSection}>
                  {people.length === 0 ? (
                    <Text style={styles.emptyGroupText}>
                      {searchQuery
                        ? "No matches in this group."
                        : "No users in this group."}
                    </Text>
                  ) : (
                    people.map((person) => (
                      <PersonRow key={person.uid} person={person} />
                    ))
                  )}
                </View>
              ) : null}
            </View>
          );
        })}
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: "#F6F7F9",
    paddingHorizontal: 24,
    paddingBottom: 24,
  },
  header: {
    width: "100%",
    maxWidth: 900,
    alignSelf: "center",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingTop: 24,
    paddingBottom: 16,
  },
  headerLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    flexShrink: 1,
  },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#fff",
    borderWidth: 1,
    borderColor: "rgba(58, 111, 177, 0.18)",
  },
  title: {
    fontFamily: "Lexend",
    fontSize: 30,
    fontWeight: "700",
    color: "#2B2B2F",
  },
  subtitle: {
    marginTop: 6,
    fontFamily: "Lexend",
    fontSize: 14,
    color: "rgba(43, 43, 47, 0.62)",
  },
  refreshButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#fff",
    borderWidth: 1,
    borderColor: "rgba(58, 111, 177, 0.18)",
  },
  searchContainer: {
    width: "100%",
    maxWidth: 900,
    alignSelf: "center",
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#fff",
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "rgba(58, 111, 177, 0.18)",
    paddingHorizontal: 12,
    height: 44,
  },
  searchIcon: { marginRight: 8 },
  searchInput: {
    flex: 1,
    fontFamily: "Lexend",
    fontSize: 14,
    color: "#2B2B2F",
    outlineStyle: "none",
  } as object,
  clearButton: { padding: 4 },
  loader: { marginTop: 16 },
  listContent: {
    width: "100%",
    maxWidth: 900,
    alignSelf: "center",
    paddingTop: 12,
    gap: 10,
  },
  groupCard: {
    backgroundColor: "#fff",
    borderRadius: 14,
    borderWidth: 1,
    borderColor: "rgba(58, 111, 177, 0.14)",
    paddingHorizontal: 16,
    paddingVertical: 14,
  },
  groupHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  groupIcon: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(58, 111, 177, 0.10)",
  },
  groupDetails: { flex: 1 },
  groupLabel: {
    fontFamily: "LexendSemiBold",
    fontSize: 16,
    color: "#2B2B2F",
  },
  groupCount: {
    fontFamily: "Lexend",
    color: "rgba(43, 43, 47, 0.55)",
  },
  groupDescription: {
    marginTop: 3,
    fontFamily: "Lexend",
    fontSize: 12,
    color: "rgba(43, 43, 47, 0.55)",
  },
  peopleSection: {
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: "rgba(58, 111, 177, 0.12)",
    gap: 10,
  },
  personRow: {
    flexDirection: "column",
  },
  personName: {
    fontFamily: "Lexend",
    fontSize: 14,
    color: "#2B2B2F",
  },
  personEmail: {
    marginTop: 2,
    fontFamily: "Lexend",
    fontSize: 12,
    color: "rgba(43, 43, 47, 0.5)",
  },
  emptyGroupText: {
    fontFamily: "Lexend",
    fontSize: 13,
    color: "rgba(43, 43, 47, 0.55)",
  },
  errorText: {
    marginTop: 12,
    fontFamily: "Lexend",
    fontSize: 13,
    color: "#C0392B",
    textAlign: "center",
  },
  accessState: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
  },
  primaryButton: {
    marginTop: 8,
    paddingHorizontal: 18,
    paddingVertical: 10,
    borderRadius: 10,
    backgroundColor: "#3a6fb1",
  },
  primaryButtonText: {
    fontFamily: "Lexend",
    fontSize: 14,
    color: "#fff",
  },
});
